import { gql } from '@apollo/client';
import { OrganizationId } from '@kaizenplatform/intelligence-entity';
import { DateTime } from 'luxon';
import GraphQLClient from '../../adapter/GraphQLClient';
import KaizenFilesClient from '../../adapter/KaizenFilesClient';
import AppendPreviewPageInput from '../../entity/AppendPreviewPageInput';
import CreateCommentInput from '../../entity/CreateCommentInput';
import CreatePagePreviewInput from '../../entity/CreatePagePreviewInput';
import CreatePositionInput from '../../entity/CreatePositionInput';
import Page from '../../entity/Page';
import PagePreview from '../../entity/PagePreview';
import Position from '../../entity/Position';
import PreviewComment from '../../entity/PreviewComment';
import PreviewCommentOrganization from '../../entity/PreviewCommentOrganization';
import UpdateCommentInput from '../../entity/UpdateCommentInput';
import User from '../../entity/User';
import sleep from '../../utility/sleep';
class PreviewCommentWebApi {
  private readonly liftReportGraphqlClient: GraphQLClient;
  private readonly kaizenFilesClient: KaizenFilesClient;

  constructor({
    liftReportGraphqlClient,
    kaizenFilesClient,
  }: {
    liftReportGraphqlClient: GraphQLClient;
    kaizenFilesClient: KaizenFilesClient;
  }) {
    this.liftReportGraphqlClient = liftReportGraphqlClient;
    this.kaizenFilesClient = kaizenFilesClient;
  }

  public getPagePreviews = async (organizationId: OrganizationId): Promise<PagePreview[]> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        query pagePreviews($organizationId: ID!) {
          pagePreviews(organizationId: $organizationId) {
            id
            title
            body
            imageUrl
          }
        }
      `,
      { organizationId },
    );

    return result.pagePreviews.map((pagePreview: PagePreview) => PagePreview.fromJSON(pagePreview));
  };

  public getPagePreview = async (id: string): Promise<PagePreview> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        query pagePreview($id: ID!) {
          pagePreview(id: $id) {
            id
            title
            body
            imageUrl
            pages {
              id
              imageUrl
              positions {
                id
                num
                x
                y
                comments {
                  id
                  author {
                    email
                    id
                    name
                    profileImageUrl
                  }
                  body
                  createdAt
                  updatedAt
                }
              }
            }
          }
        }
      `,
      { id },
    );

    return PagePreview.fromJSON(result.pagePreview);
  };

  public getPreviewComments = async (id: string): Promise<Position[]> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        query previewComments($id: ID!) {
          previewComments(id: $id) {
            positions {
              id
              x
              y
              comments {
                id
                author {
                  email
                  id
                  name
                }
                body
                createdAt
                updatedAt
              }
            }
          }
        }
      `,
      { id },
    );

    return result.previewComments.map((previewComment: Position) => Position.fromJSON(previewComment));
  };

  public createPagePreview = async (input: CreatePagePreviewInput): Promise<PagePreview> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        mutation createPagePreview($input: CreatePagePreviewInput!) {
          createPagePreview(input: $input) {
            id
            title
            body
            imageUrl
            pages {
              id
              imageUrl
              positions {
                id
                x
                y
                comments {
                  id
                  author {
                    email
                    id
                    name
                  }
                  body
                  createdAt
                  updatedAt
                }
              }
            }
          }
        }
      `,
      { input },
    );

    return PagePreview.fromJSON(result.createPagePreview);
  };

  public createPosition = async (input: CreatePositionInput): Promise<Position> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        mutation createPosition($input: CreatePositionInput!) {
          createPosition(input: $input) {
            id
            num
            x
            y
            comments {
              id
              author {
                email
                id
                name
                profileImageUrl
              }
              body
              createdAt
              updatedAt
            }
          }
        }
      `,
      { input },
    );

    return Position.fromJSON(result.createPosition);
  };

  public createComment = async (input: CreateCommentInput): Promise<PreviewComment> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        mutation createComment($input: CreateCommentInput!) {
          createComment(input: $input) {
            id
            author {
              email
              id
              name
            }
            body
            createdAt
            updatedAt
          }
        }
      `,
      { input },
    );

    return PreviewComment.fromJSON(result.createComment);
  };

  public appendPreviewPage = async (input: AppendPreviewPageInput): Promise<Page> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        mutation appendPreviewPage($input: AppendPreviewPageInput!) {
          appendPreviewPage(input: $input) {
            id
            imageUrl
            positions {
              id
              x
              y
              comments {
                id
                author {
                  email
                  id
                  name
                }
                body
                createdAt
                updatedAt
              }
            }
          }
        }
      `,
      { input },
    );
    return Page.fromJSON(result.appendPreviewPage);
  };

  public updateComment = async (input: UpdateCommentInput): Promise<PreviewComment> => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        mutation updateComment($input: UpdateCommentInput!) {
          updateComment(input: $input) {
            id
            author {
              email
              id
              name
            }
            body
            createdAt
            updatedAt
          }
        }
      `,
      { input },
    );

    return PreviewComment.fromJSON(result.updateComment);
  };

  public uploadFile = async (file: File, bucketId: string, organizationId: string) => {
    const result = await this.kaizenFilesClient.upload(file, bucketId, organizationId);
    return result;
  };

  public getPreviewCommentOrganization = async (organizationId: string) => {
    const result = await this.liftReportGraphqlClient.query(
      gql`
        query getOrganizationById($id: ID!) {
          organization(id: $id) {
            id
            name
            bucketId
          }
        }
      `,
      { id: organizationId },
    );

    return PreviewCommentOrganization.fromJSON(result.organization);
  };

  public pagePreviewsStub = async (): Promise<PagePreview[]> => {
    await sleep(2000);
    return [
      PagePreview.fromJSON({
        id: 'sample',
        title: 'サンプルページ_A',
        body: 'memo_memo_memo_memo_memo...',
        imageUrl: 'https://......',
        pages: [
          Page.fromJSON({
            id: '1',
            imageUrl: 'https://......',
            positions: [
              Position.fromJSON({
                id: 'sample',
                num: 1,
                x: 100,
                y: 100,
                comments: [
                  PreviewComment.fromJSON({
                    id: 'abc',
                    author: User.fromJSON({
                      email: 'yuya.furui@x-garden.co.jp',
                      id: 'hoge',
                      name: 'Yuya Furui',
                      profileImageUrl: '',
                    }),
                    body: '〇〇を▲▲した方が良さそう',
                    createdAt: DateTime.fromObject({
                      year: 2021,
                      month: 10,
                      day: 31,
                    }).toLocaleString(),
                    updatedAt: DateTime.fromObject({
                      year: 2021,
                      month: 10,
                      day: 31,
                    }).toLocaleString(),
                  }),
                ],
              }),
            ],
          }),
        ],
      }),
    ];
  };
}

export default PreviewCommentWebApi;
